// Merges the two dictionary halves into one lookup keyed by segment id, and
// exposes the three functions the parser/validator/UI use to explain a
// segment, an element, or a coded value. Every lookup has a defined fallback
// — nothing here returns `undefined`, so a caller never needs to branch on
// "not found" just to render something.

import {HEADER_SEGMENTS} from './header-segments';
import {ITEM_LOOP_SEGMENTS} from './item-loop-segments';
import {HEADER_SEGMENTS_852} from './852-header-segments';
import {ITEM_ACTIVITY_SEGMENTS_852} from './852-item-activity-segments';
import {
  unknownElementDefinition,
  unknownSegmentDefinition,
  type ElementDefinition,
  type SegmentDefinition,
} from './types';

const SEGMENT_DEFINITIONS: Record<string, SegmentDefinition> = {
  ...HEADER_SEGMENTS,
  ...ITEM_LOOP_SEGMENTS,
  ...HEADER_SEGMENTS_852,
  ...ITEM_ACTIVITY_SEGMENTS_852,
};

export function getSegmentDefinition(segmentId: string): SegmentDefinition {
  return SEGMENT_DEFINITIONS[segmentId] ?? unknownSegmentDefinition(segmentId);
}

export function getElementDefinition(
  segmentId: string,
  position: number
): ElementDefinition {
  const found = SEGMENT_DEFINITIONS[segmentId]?.elements.find(
    e => e.position === position
  );
  return found ?? unknownElementDefinition(segmentId, position);
}

/** Resolves a raw coded value to its plain-English label. Falls back to the
 * raw code itself when the segment/position isn't in the dictionary or the
 * code isn't in that element's list — the caller always gets a string to
 * display, never `undefined`. The match against the code list trims
 * incidental surrounding whitespace first (matching validator.ts's
 * tolerance) and case-folds (so a lowercase/mixed-case value like 'zz'
 * still finds the list's 'ZZ' entry, keeping this in agreement with
 * validator.ts's case-insensitive ZZ exemption), so a padded-but-known code
 * still renders its label rather than falling back as if unrecognized; the
 * fallback returns the original, untrimmed value. */
export function getCodeLabel(
  segmentId: string,
  position: number,
  code: string
): string {
  const element = getElementDefinition(segmentId, position);
  const trimmedCode = code.trim().toUpperCase();
  return (
    element.codes?.find(c => c.code.toUpperCase() === trimmedCode)?.label ??
    code
  );
}

export {LoopId} from './types';
export type {
  CodeListEntry,
  ElementDefinition,
  ElementDataType,
  SegmentDefinition,
} from './types';
